import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AdminAnnouncementState } from './admin-announcement.state';
import { AdminAnnouncementDTO, PageableDTO } from '../../../openapi/shell-service-openapi';
import { AnnouncementEntitiesSelectors } from '../../../store/announcement-entities';

/**
 * Feature-Key für den Admin-Announcement State.
 */
export const ADMIN_ANNOUNCEMENT_FEATURE_KEY = 'adminAnnouncement';

/**
 * Selectors für das Admin-Announcement Dashboard.
 *
 * Detail-Daten werden über AnnouncementDetailEntitiesSelectors abgefragt.
 */
export class AdminAnnouncementSelectors {
  private static readonly _feature = createFeatureSelector<AdminAnnouncementState>(
    ADMIN_ANNOUNCEMENT_FEATURE_KEY,
  );

  /** Aktuelle Seite. */
  static readonly pageIndex = createSelector(
    this._feature,
    (state) => state.pageIndex,
  );

  /** Anzahl Einträge pro Seite. */
  static readonly pageSize = createSelector(
    this._feature,
    (state) => state.pageSize,
  );

  /** Berechnetes Pageable-Objekt für API-Aufrufe (inkl. Sortierung). */
  static readonly pageable = createSelector(
    this._feature,
    (state): PageableDTO => ({
      number: state.pageIndex,
      size: state.pageSize,
      property: [...state.sortProperties],
      direction: [...state.sortDirections],
    }),
  );

  /** IDs der anzuzeigenden Entities in Server-Reihenfolge. */
  static readonly displayedIds = createSelector(
    this._feature,
    (state) => state.displayedIds,
  );

  /** Angezeigte Announcements in Server-Reihenfolge (Join von displayedIds + Entity-Store). */
  static readonly displayedAnnouncements = createSelector(
    this.displayedIds,
    AnnouncementEntitiesSelectors.announcementEntities,
    (ids, entities): AdminAnnouncementDTO[] =>
      ids.map(id => entities[id]).filter((e): e is AdminAnnouncementDTO => !!e),
  );

  /** Aktueller Editor-Modus. */
  static readonly editorMode = createSelector(
    this._feature,
    (state) => state.editorMode,
  );

  /** ID des Announcements im Edit-Modus. */
  static readonly editingAnnouncementId = createSelector(
    this._feature,
    (state) => state.editingAnnouncementId,
  );

  /** Speicher-Zustand. */
  static readonly isSaving = createSelector(
    this._feature,
    (state) => state.isSaving,
  );

  /** Fehlermeldung beim Speichern. */
  static readonly saveError = createSelector(
    this._feature,
    (state) => state.saveError,
  );
}
